import { useState } from 'react';
import { Modal, Input, Typography, Steps, Tag, Alert, Space, Button } from 'antd';
import {
    ExclamationCircleOutlined,
    LoadingOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    MinusCircleOutlined,
    KeyOutlined,
    DatabaseOutlined,
    PictureOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';

const { Paragraph, Text } = Typography;

const SYSTEMS = {
    keycloak: { label: 'Login', color: 'gold', icon: <KeyOutlined /> },
    database: { label: 'Vellby data', color: 'blue', icon: <DatabaseOutlined /> },
    storage: { label: 'Files', color: 'purple', icon: <PictureOutlined /> },
};

// Backend step status -> antd Steps status + icon. Set explicitly per item
// (not via `current`) because steps can be skipped, fail mid-list, or be
// reverted to 'wait' when the database transaction rolls back.
function stepStatus(status, willSkip) {
    switch (status) {
        case 'process':
            return { status: 'process', icon: <LoadingOutlined /> };
        case 'finish':
            return { status: 'finish', icon: <CheckCircleFilled /> };
        case 'skipped':
            return { status: 'finish', icon: <MinusCircleOutlined style={{ color: 'var(--text-secondary)' }} /> };
        case 'error':
            return { status: 'error', icon: <CloseCircleFilled /> };
        default:
            return { status: 'wait', icon: willSkip ? <MinusCircleOutlined /> : undefined };
    }
}

/**
 * Password-confirmed self-service account deletion, shown as a checklist.
 *
 *   1. password — verified against Keycloak server-side before anything is shown
 *   2. review   — every part that will be removed (login, messages, DMs,
 *                 connections, profile, ...) as a vertical flow
 *   3. running  — the backend streams progress and each step is ticked off;
 *                 on success the user signs out from the finished checklist
 *
 * Shared by the account page's Danger zone card and the banner on the
 * user's own person entity page.
 *
 *   <DeleteAccountModal open={open} onClose={() => setOpen(false)} />
 */
function DeleteAccountModal({ open, onClose }) {
    const { fetchDeletionPlan, deleteAccount, signOutDeletedAccount } = useAuth();
    const [phase, setPhase] = useState('password'); // password | review | running | done | failed
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [plan, setPlan] = useState(null);
    const [progress, setProgress] = useState({});
    const [result, setResult] = useState(null);

    const locked = phase === 'running' || busy;

    const reset = () => {
        setPhase('password');
        setPassword('');
        setError(null);
        setPlan(null);
        setProgress({});
        setResult(null);
    };

    const close = () => {
        if (locked) return;
        if (phase === 'done') {
            signOutDeletedAccount();
            return;
        }
        reset();
        onClose();
    };

    const loadPlan = async () => {
        if (!password) return;
        setBusy(true);
        setError(null);
        try {
            setPlan(await fetchDeletionPlan(password));
            setPhase('review');
        } catch (err) {
            setError(err.message || 'Could not prepare account deletion');
        } finally {
            setBusy(false);
        }
    };

    const handleEvent = (event) => {
        if (event.type === 'plan') {
            // Re-gathered server-side right before deleting; trust the latest view.
            setPlan(prev => ({ ...prev, person: event.person, steps: event.steps }));
        } else if (event.type === 'step') {
            setProgress(prev => ({ ...prev, [event.key]: { status: event.status, detail: event.detail } }));
        }
    };

    const runDeletion = async () => {
        setPhase('running');
        setProgress({});
        setError(null);
        try {
            const done = await deleteAccount(password, handleEvent);
            setResult(done);
            setPhase(done.ok ? 'done' : 'failed');
        } catch (err) {
            setResult({ ok: false, error: err.message || 'Failed to delete account' });
            setPhase('failed');
        }
    };

    const stepItems = (plan?.steps || []).map(step => {
        const system = SYSTEMS[step.system] || SYSTEMS.database;
        const live = progress[step.key];
        const pendingSkip = step.will_skip && !live;
        const { status, icon } = stepStatus(live?.status, step.will_skip);
        return {
            status,
            icon,
            title: (
                <Space size={8} wrap>
                    <span style={{ color: pendingSkip ? 'var(--text-secondary)' : undefined }}>{step.title}</span>
                    <Tag color={system.color} icon={system.icon} style={{ marginInlineEnd: 0 }}>{system.label}</Tag>
                    {step.count > 0 && <Tag style={{ marginInlineEnd: 0 }}>{step.count}</Tag>}
                </Space>
            ),
            content: (
                <div style={{ paddingBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {step.description}{pendingSkip ? ' — nothing to remove' : ''}
                    </Text>
                    {step.items?.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {step.items.map((item, i) => <Tag key={i} style={{ marginInlineEnd: 0 }}>{item}</Tag>)}
                            {step.more > 0 && <Tag style={{ marginInlineEnd: 0 }}>+{step.more} more</Tag>}
                        </div>
                    )}
                    {live?.detail && (
                        <div style={{ marginTop: 4 }}>
                            <Text type={live.status === 'error' ? 'danger' : undefined} style={{ fontSize: 12 }}>
                                {live.detail}
                            </Text>
                        </div>
                    )}
                </div>
            ),
        };
    });

    const footer = {
        password: (
            <Space>
                <Button onClick={close} disabled={busy}>Cancel</Button>
                <Button danger type="primary" loading={busy} disabled={!password} onClick={loadPlan}>
                    Continue
                </Button>
            </Space>
        ),
        review: (
            <Space>
                <Button onClick={close}>Cancel</Button>
                <Button danger type="primary" icon={<DeleteOutlined />} onClick={runDeletion}>
                    Delete my account
                </Button>
            </Space>
        ),
        running: <Button danger type="primary" loading>Deleting…</Button>,
        done: <Button type="primary" onClick={close}>Sign out</Button>,
        failed: (
            <Space>
                <Button onClick={close}>Close</Button>
                {/* Once the login is gone the password can't be re-verified, so only a superadmin can finish. */}
                {progress.keycloak_user?.status !== 'finish' && (
                    <Button danger type="primary" onClick={runDeletion}>Retry</Button>
                )}
            </Space>
        ),
    }[phase];

    return (
        <Modal
            title={
                <span>
                    <ExclamationCircleOutlined style={{ color: 'var(--ant-color-error)', marginRight: 8 }} />
                    {phase === 'done' ? 'Account deleted' : 'Delete your account?'}
                </span>
            }
            open={open}
            onCancel={close}
            footer={footer}
            width={phase === 'password' ? 480 : 600}
            maskClosable={false}
            closable={!locked}
            keyboard={!locked}
        >
            {phase === 'password' ? (
                <>
                    <Paragraph>
                        This permanently deletes your login and all data tied to your account. This
                        action cannot be undone. You'll see everything that will be removed before
                        anything is deleted.
                    </Paragraph>
                    <Text strong>Enter your password to continue:</Text>
                    <Input.Password
                        autoFocus
                        style={{ marginTop: 8 }}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onPressEnter={loadPlan}
                        disabled={busy}
                        placeholder="Current password"
                        status={error ? 'error' : undefined}
                    />
                    {error && <Text type="danger" style={{ display: 'block', marginTop: 8 }}>{error}</Text>}
                </>
            ) : (
                <>
                    {plan?.last_admin && phase === 'review' && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            title="You're the last admin of this node"
                            description="If you delete your account, no one will be able to access or administer this node. Make someone else an admin first if you want it to keep running."
                        />
                    )}
                    {phase === 'review' && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            title="This will be permanently deleted"
                            description="Groups you belong to stay; only your membership is removed. Direct-message conversations are deleted for both people."
                        />
                    )}
                    {phase === 'done' && (
                        <Alert
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                            title="Your account and data were deleted"
                            description="You'll be signed out now."
                        />
                    )}
                    {phase === 'failed' && (
                        <Alert type="error" showIcon style={{ marginBottom: 16 }} title="Deletion stopped" description={result?.error} />
                    )}
                    <Steps orientation="vertical" size="small" items={stepItems} />
                </>
            )}
        </Modal>
    );
}

export default DeleteAccountModal;
